// BaseMapDataOverlaysModule.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
// ───────────────────────────────────────────────────────────────────────────
//  mapDataOverlays Vuex module  –  WITH FULL PERSISTENCE
// ───────────────────────────────────────────────────────────────────────────

import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import emitter              from '../../../../utils/bus';
import graph_client         from '../../../../utils/graphql/graph_client';
import { GetTrail }         from '../../../../utils/graphql/trail-graph';
import { GetPtv2PlannedTrail } from '../../../../utils/graphql/planned-trail-graph';
import { GetRecordedTrailByRecordingId } from '../../../../utils/graphql/recorded-trail-graph';
import {
  addHiiker, addSlope, addWeather, addCell,
  removeCell, removeHiiker, removeSlope, removeWeather,
  darkenColor, safeJsonParse
} from '../../helpers/BaseMapDialogHelpers'
import {isMapReady} from "../../../../utils/mapReadyHelper";
import {MapLayerCategory} from "../../../../utils/layer-registry";

type RootState = any;


// ──────────────────────────── State ─────────────────────────────
export interface BaseMapDataOverlaysState {
  /* original */
  activeSources: string[];
  trailOptions : Record<string,string>;
  sourceColors : Record<string,string>;
  miniLoader   : boolean;

  /* overlay flags - now persistent */
  hiikerTrailsActive : boolean;
  cellCoverageActive : boolean;
  slopeAngleActive   : boolean;
  activeWeatherLayer : { name:string|null; type:string|null };
}

const state: BaseMapDataOverlaysState = {
  // Load persisted state or use defaults
  activeSources: safeJsonParse('activeTrailSources', []),

  trailOptions : {
    get_user_recordings      : 'Recordings',
    get_user_planned_trails  : 'Planned',
    get_user_completed_trails: 'Completed',
  },

  sourceColors : {
    get_user_recordings      : '#9E00FF',
    get_user_planned_trails  : '#F57700',
    get_user_completed_trails: '#FF00FF',
  },

  miniLoader          : false,

  // Load persisted overlay flags or use defaults
  hiikerTrailsActive  : safeJsonParse('hiikerTrailsActive', false),
  cellCoverageActive  : safeJsonParse('cellCoverageActive', false),
  slopeAngleActive    : safeJsonParse('slopeAngleActive', false),
  activeWeatherLayer  : safeJsonParse('activeWeatherLayer', { name:null, type:null }),
};

let mapClickHandlerRef: ((e: any) => void) | null = null;

// ─────────────────────────── Mutations ──────────────────────────
const mutations: MutationTree<BaseMapDataOverlaysState> = {
  /* original - persist trail sources */
  SET_ACTIVE_SOURCES (s, srcs:string[]){
    s.activeSources = srcs;
    localStorage.setItem('activeTrailSources', JSON.stringify(srcs));
  },
  ADD_ACTIVE_SOURCE (s, src:string) {
    if(!s.activeSources.includes(src)){
      s.activeSources.push(src);
      localStorage.setItem('activeTrailSources', JSON.stringify(s.activeSources));
    }
  },
  REMOVE_ACTIVE_SOURCE(s,src:string) {
    s.activeSources = s.activeSources.filter(x => x !== src);
    localStorage.setItem('activeTrailSources', JSON.stringify(s.activeSources));
  },
  SET_MINI_LOADER (s,v:boolean) { s.miniLoader=v; },

  /* NEW: Persist other overlay flags */
  SET_HIIKER_TRAILS_ACTIVE (s,v:boolean){
    s.hiikerTrailsActive = v;
    localStorage.setItem('hiikerTrailsActive', JSON.stringify(v));
  },
  SET_CELL_COVERAGE_ACTIVE(s,v:boolean){
    s.cellCoverageActive = v;
    localStorage.setItem('cellCoverageActive', JSON.stringify(v));
  },
  SET_SLOPE_ANGLE_ACTIVE  (s,v:boolean){
    s.slopeAngleActive = v;
    localStorage.setItem('slopeAngleActive', JSON.stringify(v));
  },
  SET_ACTIVE_WEATHER_LAYER(s,p:{name:string|null;type:string|null}){
    s.activeWeatherLayer = p;
    localStorage.setItem('activeWeatherLayer', JSON.stringify(p));
  },
};

// ─────────────────────────── Actions ────────────────────────────
const actions: ActionTree<BaseMapDataOverlaysState,RootState> = {

  // ── overlay toggles (called by component) - Commit mutations which now handle persistence ──
  toggleSlopeOverlay   ({commit,state}){const nxt=!state.slopeAngleActive; commit('SET_SLOPE_ANGLE_ACTIVE',nxt); nxt?addSlope():removeSlope();},
  toggleHiikerTrailsOverlay({commit,state}){const nxt=!state.hiikerTrailsActive; commit('SET_HIIKER_TRAILS_ACTIVE',nxt); nxt?addHiiker():removeHiiker();},
  toggleCellCoverageOverlay({commit,state}){const nxt=!state.cellCoverageActive; commit('SET_CELL_COVERAGE_ACTIVE',nxt); nxt?addCell():removeCell();},
  toggleWeatherOverlay ({commit,state},p:{type:string,name:string}){
    const isCurrentlyActive = state.activeWeatherLayer.name === p.name;
    const newVal = isCurrentlyActive ? {name:null,type:null} : p;
    const oldName = state.activeWeatherLayer.name; // Get name before mutation

    commit('SET_ACTIVE_WEATHER_LAYER', newVal); // This now persists

    // Remove old layer if it existed and is different from the new one (or if new one is null)
    if (oldName && oldName !== newVal.name) {
      removeWeather(oldName);
    }
    // Add new layer if it's not null
    if (newVal.name && newVal.type) {
      addWeather(newVal.type, newVal.name);
    }
  },

  // ── overlay listener lifecycle ──
  initOverlayListeners({dispatch, state}){
    const applyPersistedStateToMap = () => {
      console.log("Attempting to apply persisted state to map..."); // DEBUG LOG
      // Add overlays based on the *current state* (which was loaded from localStorage)
      // Check map readiness more carefully
      if (!isMapReady(true)) {
        console.log("Map or style not ready yet."); // DEBUG LOG
        return; // Don't proceed if map/style isn't fully ready
      }

      console.log("Map ready, applying overlays:", {
        slope: state.slopeAngleActive,
        hiiker: state.hiikerTrailsActive,
        cell: state.cellCoverageActive,
        weather: state.activeWeatherLayer
      }); // DEBUG LOG

      // Apply non-trail overlays
      state.slopeAngleActive   ? addSlope()  : removeSlope(); // Ensure removal if false
      state.hiikerTrailsActive ? addHiiker() : removeHiiker(); // Ensure removal if false
      state.cellCoverageActive ? addCell()   : removeCell();   // Ensure removal if false

      // Apply weather overlay
      // Remove any potentially lingering weather layer first
      // (This might be redundant if destroyOverlayListeners is always called, but adds safety)
      removeWeather('temperature'); // Example: remove all possible types if unsure
      removeWeather('cloud');
      removeWeather('rainfall');
      removeWeather('snowfall');
      removeWeather('wind');
      // Add the active one
      if (state.activeWeatherLayer.name && state.activeWeatherLayer.type) {
        addWeather(state.activeWeatherLayer.type, state.activeWeatherLayer.name);
      }

      // Restore trail sources - this is where your original issue might be
      console.log("Dispatching refreshLineSources for trails:", state.activeSources); // DEBUG LOG
      dispatch('refreshLineSources');

      // Re-initialize click handler AFTER layers might have been added
      dispatch('initMapClickHandler');
    };

    const onLoaded = () => {
      setTimeout(() => {
        console.log("Map loaded event received, applying persisted state..."); // DEBUG LOG

        dispatch('initActiveSources');
        // Try to apply persisted state to map
        applyPersistedStateToMap();
      }, 2500)
    };

    const onStyle = () => {
      setTimeout(() => {
        console.log("map-style-loaded event received."); // DEBUG LOG
        // Try to apply persisted state to map
        applyPersistedStateToMap();
      }, 2500)
    };

    // --- Event Listener Setup ---
    // Ensure prior listeners are removed before adding new ones if this action can be called multiple times
    if ((actions as any)._unsubscribeMapHandlers) {
      (actions as any)._unsubscribeMapHandlers();
    }

    emitter.on('map-loaded', onLoaded);
    emitter.on('map-style-loaded', onStyle);

    // Store the unsub functions
    (actions as any)._unsubscribeMapHandlers = () => {
      emitter.off('map-loaded', onLoaded);
      emitter.off('map-style-loaded', onStyle);
      console.log("Map loaded/style listeners removed."); // DEBUG LOG
      // Clear the stored function itself
      delete (actions as any)._unsubscribeMapHandlers;
    };

    // Initial attempt to apply state in case map is already loaded when component mounts
    applyPersistedStateToMap();
  },

  destroyOverlayListeners({ state }){
    // Unsubscribe from map events
    if ((actions as any)._unsubscribeMapHandlers) {
      (actions as any)._unsubscribeMapHandlers();
    }

    // Remove click handler
    if (mapClickHandlerRef && window.map) {
      window.map.off('click', mapClickHandlerRef);
      mapClickHandlerRef = null;
      console.log("Map click handler removed."); // DEBUG LOG
    }

    // Clean up layers from the map (optional but good practice)
    // These are already called if toggling OFF, but ensures cleanup on component destroy
    // removeSlope();
    // removeHiiker();
    // removeCell();
    // if(state.activeWeatherLayer.name) {
    //   removeWeather(state.activeWeatherLayer.name);
    // }
    // Note: Trail source removal happens via toggleLineSource when activeSources changes
    // or potentially needs manual cleanup here if component unmounts unexpectedly.
    console.log("Overlay listeners destroyed."); // DEBUG LOG
  },

  // ────────────────────── ORIGINAL trail-source logic ─────────────────────
  // (No changes needed below here unless debugging reveals issues in toggleLineSource)

  initActiveSources({ state, commit }) {
    const validSources = Object.keys(state.trailOptions);
    // No user check needed if state is already loaded from localStorage?
    // Or keep it if anonymous users shouldn't have trails saved.
    // if (!window.user) { commit('SET_ACTIVE_SOURCES', []); return; }

    const urlParams = new URLSearchParams(window.location.search);
    let useUrl = false; const fromUrl:string[]=[];
    validSources.forEach(src=>{
      if(urlParams.has(src)){ useUrl=true; if(urlParams.get(src)==='true') fromUrl.push(src);}
    });

    if(useUrl){
      console.log("Loading active sources from URL params:", fromUrl); // DEBUG LOG
      commit('SET_ACTIVE_SOURCES', fromUrl); // SET_ACTIVE_SOURCES now persists
    } else {
      // State is already loaded from localStorage, just ensure validity?
      // Or perhaps this action is primarily for URL param override?
      // If state is primary source, this might only need to filter invalid sources.
      const loadedFromStorage = state.activeSources; // Already parsed
      const filtered = loadedFromStorage.filter(s => validSources.includes(s));
      if (filtered.length !== loadedFromStorage.length) {
        console.log("Filtered invalid active sources from localStorage:", filtered); // DEBUG LOG
        // Commit only if filtering actually changed something
        commit('SET_ACTIVE_SOURCES', filtered);
      } else {
        console.log("Active sources loaded from localStorage:", loadedFromStorage); // DEBUG LOG
      }
    }
  },

  forceOneSource({commit,dispatch},src:string){ commit('SET_ACTIVE_SOURCES',[src]); dispatch('refreshLineSources'); },

  toggleTrail({state,commit,dispatch},src:string){
    // Use mutations which now handle persistence
    state.activeSources.includes(src) ? commit('REMOVE_ACTIVE_SOURCE', src) : commit('ADD_ACTIVE_SOURCE', src);
    // Apply the change to the map
    dispatch('toggleLineSource', src);
  },

  /**
   * Show or hide a personal-trail vector source (and its 2–3 layers).
   *
   *  ┌─────────────┐  z: 1  white halo     (borderId)
   *  │  TRAIL      │  z: 0  coloured line  (layerId)
   *  └─────────────┘  z:-1  optional pts   (pointId)   ← if you ever add it
   *
   * The helper is idempotent: calling it twice with the same state does nothing.
   */
  toggleLineSource({ state, getters }, src: string) {
    if (!window.map || !window.layerRegistry || !window.user) {
      console.error('toggleLineSource ➜ map, registry, or user missing');
      return;
    }

    const layerId  = src;            // main coloured stroke
    const borderId = `${src}-border`;
    const namesId = `${src}-names`;     // <-- New ID for the names layer

    /* ───────────────── construct the vector source spec ───────────── */
    const baseUrl  = window.location.origin;
    const tilesUrl = `${baseUrl}/vector/${src}/{z}/{x}/{y}?public_filter=false&user_id=${window.user.id}&t=${Date.now()}`;

    const sourceSpec = {
      type:    'vector',
      tiles:   [tilesUrl],
      minzoom: getters.minZoomForSource(src),
      maxzoom: 14
    };

    /* ───────────────── colour palette for this trail ──────────────── */
    const color = state.sourceColors[src] || '#000000';
    const halo  = darkenColor(color);

    /* ───────────────── ADD requested  ─────────────────────────────── */
    if (state.activeSources.includes(src)) {
      /* main line (z:1) – will be no-op if already added */
      window.layerRegistry.addLayer(
        {
          id: layerId,
          type: 'line',
          source: src,
          'source-layer': 'data',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': color,
            'line-width': ['interpolate', ['linear'], ['zoom'], 0, 1, 14, 4]
          }
        },
        MapLayerCategory.Annotations,
        {
          z: 1,
          // @ts-ignore
          source: { id: src, spec: sourceSpec }      // create once
        }
      );

      /* halo (z:0) */
      window.layerRegistry.addLayer(
        {
          id: borderId,
          type: 'line',
          source: src,
          'source-layer': 'data',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': halo, // tweak to taste
            'line-width': 6 // Wider than main line to create halo effect
          }
        },
        MapLayerCategory.Annotations,
        { z: 0 }                                     // sits above main
      );

      /* TRAIL NAMES - Symbol layer (z:2) */
      window.layerRegistry.addLayer(
        {
          id: namesId,
          type: 'symbol',
          source: src,
          'source-layer': 'data',
          minzoom: 9, // <-- Only show at zoom 12 and above
          layout: {
            'symbol-placement': 'line',
            'text-field': ['get', 'title'], // Get text from the 'name' property
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-size': 12,
            'text-allow-overlap': false,
            'symbol-spacing': 250,
            'text-max-angle': 30,
            'text-pitch-alignment': 'map',
            'text-ignore-placement': false // Ensure labels aren't ignored due to minor collisions if possible
          },
          paint: {
            'text-color': '#333333', // Dark grey text
            'text-halo-color': 'rgba(255, 255, 255, 0.8)', // White halo
            'text-halo-width': 1.5
          }
        },
        MapLayerCategory.Annotations, // Keep in the same category
        {
          z: 2 // Draw names above the main line
          // No source needed here either
        }
      );

      /* optional point layer could go here with z:-1 */

      /* ───────────────── REMOVE requested  ──────────────────────────── */
    } else {
      window.layerRegistry.removeLayer(borderId); // halo first
      window.layerRegistry.removeLayer(layerId);  // main
      window.layerRegistry.removeLayer(namesId); // names
      /* If these were the only layers using `src`, the registry
         automatically removes the source when it becomes orphaned. */
    }
  },

  addFeatureFromClick({ commit, dispatch }, { source, properties }) {
    commit('SET_MINI_LOADER', true);

    // Ensure properties exists and has an id or equivalent
    if (!properties) {
      console.warn(`Clicked feature from source ${source} has no properties.`);
      commit('SET_MINI_LOADER', false);
      return;
    }
    // Determine the ID property based on the source
    let id = null;
    switch (source) {
      case 'get_user_recordings':       id = properties.id; break;
      case 'get_user_planned_trails':   id = properties.ptv2_planned_trail_id; break;
      case 'get_user_completed_trails': id = properties.line_stringable_id; break;
      case 'get_published_trails':      id = properties.trail_id; break;
      case 'hiiker-trails-source':      // Check your actual vector tile source properties for HiiKER trails
        id = properties.id || properties.trail_id || properties.objectid; // Example possibilities
        break;
      default:
        console.warn(`Unhandled click source: ${source}`);
        commit('SET_MINI_LOADER', false);
        return;
    }

    if (id === null || id === undefined) {
      console.warn(`Could not determine ID for clicked feature from source ${source}:`, properties);
      commit('SET_MINI_LOADER', false);
      return;
    }

    console.log(`Feature clicked - Source: ${source}, ID: ${id}`); // DEBUG LOG

    // Dispatch based on source
    switch (source) {
      case 'get_user_recordings':       dispatch('getRecording', id); break;
      case 'get_user_planned_trails':   dispatch('getPtv2PlannedTrail', id); break;
      case 'get_user_completed_trails': dispatch('getTrail', id); break; // Assumes uses GetTrail
      case 'get_published_trails':      dispatch('getTrail', id); break; // Assumes uses GetTrail
      case 'hiiker-trails-source':      dispatch('getTrail', id); break; // Assumes HiiKER trails use GetTrail
    }
  },


  getTrail({dispatch,commit},id:string){
    console.log("Fetching trail with ID:", id); // DEBUG LOG
    graph_client.request(GetTrail,{id}).then((d)=>{
      // @ts-ignore
      if(d?.trail?.multilinestring){
        // @ts-ignore
        dispatch('addLineLayToMap',JSON.parse(d.trail.multilinestring));
        // @ts-ignore
        window.trailCardDataTrail?.(d.trail); // Use optional chaining
      } else { alert('Error parsing trail'); console.error("Error parsing trail data:", d); }
    }).catch((err)=> { alert('Error fetching trail'); console.error("Error fetching trail:", err); })
      .finally(()=>commit('SET_MINI_LOADER',false));
  },

  getPtv2PlannedTrail({dispatch,commit},id:string){
    console.log("Fetching PTV2 planned trail with ID:", id); // DEBUG LOG
    graph_client.request(GetPtv2PlannedTrail,{id}).then(d=>{
      // @ts-ignore
      if(d?.ptv2PlannedTrailById?.geojsonFeatureCollection){
        // @ts-ignore
        dispatch('addLineLayToMap',d.ptv2PlannedTrailById.geojsonFeatureCollection);
        // @ts-ignore
        window.trailCardDataPtv2PlannedTrail?.(d.ptv2PlannedTrailById); // Use optional chaining
      } else { alert('Error parsing planned trail'); console.error("Error parsing PTV2 planned trail data:", d); }
    }).catch((err)=> { alert('Error fetching planned trail'); console.error("Error fetching PTV2 planned trail:", err); })
      .finally(()=>commit('SET_MINI_LOADER',false));
  },

  getRecording({dispatch,commit},id:string){
    console.log("Fetching recording with ID:", id); // DEBUG LOG
    // Ensure ID is string if GraphQL expects it
    graph_client.request(GetRecordedTrailByRecordingId,{id:`${id}`}).then(d=>{
      // @ts-ignore
      if(d?.recording?.lineString){
        // @ts-ignore
        dispatch('addLineLayToMap',JSON.parse(d.recording.lineString));
        // @ts-ignore
        window.trailCardDataRecording?.(d.recording); // Use optional chaining
      } else { alert('Error parsing recording'); console.error("Error parsing recording data:", d); }
    }).catch((err)=> { alert('Error fetching recording'); console.error("Error fetching recording:", err); })
      .finally(()=>commit('SET_MINI_LOADER',false));
  },

  removeLineLayer(){
    if(!window.map||!window.map.isStyleLoaded())return;
    console.log("Removing individual line layer"); // DEBUG LOG
    // Check existence before removing to prevent errors
    if (window.map.getLayer('individual-line-border')) window.map.removeLayer('individual-line-border');
    if (window.map.getLayer('individual-line')) window.map.removeLayer('individual-line');
    if (window.map.getSource('individual-line')) window.map.removeSource('individual-line');
  },

  addLineLayToMap({dispatch},fc:any){
    if(!window.map||!window.map.isStyleLoaded())return;
    console.log("Adding individual line layer to map"); // DEBUG LOG
    dispatch('removeLineLayer'); // Ensure previous one is gone
    const color='#ff00f2',dark=darkenColor(color);
    try {
      window.map.addSource('individual-line',{type:'geojson',data:fc});
      window.map.addLayer({id:'individual-line-border',type:'line',source:'individual-line',
        layout:{'line-join':'round','line-cap':'round'},
        paint:{'line-color':dark,'line-width':['interpolate',['linear'],['zoom'],0,4,14,10]}});
      window.map.addLayer({id:'individual-line',type:'line',source:'individual-line',
        layout:{'line-join':'round','line-cap':'round'},
        paint:{'line-color':color,'line-width':['interpolate',['linear'],['zoom'],0,2,14,8]}});
    } catch (error) {
      console.error("Error adding individual line layer:", error); // DEBUG LOG
      // Clean up source if layer adding failed
      if (window.map.getSource('individual-line')) window.map.removeSource('individual-line');
    }
  },

  refreshLineSources({state, dispatch}){
    console.log("Refreshing line sources:", state.activeSources); // DEBUG LOG
    state.activeSources.forEach(s => {
      if (!window.map.getSource(s)) {
        console.log(`Source ${s} needs to be added during refresh.`); // DEBUG LOG
        dispatch('toggleLineSource', s);
      } else {
        // Optional: Ensure layers are visible if source exists
        const layerId = s;
        const borderId = `${s}-border`;
        if (window.map.getLayer(layerId)) window.map.setLayoutProperty(layerId, 'visibility', 'visible');
        if (window.map.getLayer(borderId)) window.map.setLayoutProperty(borderId, 'visibility', 'visible');
      }
    });
  },


  initMapClickHandler({ state, dispatch, rootState }) {
    if (!window.map) { console.error('Map missing for initMapClickHandler'); return; }

    // Remove the previous handler to prevent duplicates
    if (mapClickHandlerRef) {
      window.map.off('click', mapClickHandlerRef);
      console.log("Removed existing map click handler."); // DEBUG LOG
      mapClickHandlerRef = null;
    }

    const HIT_RADIUS = 6;

    // Define the handler function
    mapClickHandlerRef = (e: any) => {
      // Prevent clicks during planning mode
      window.removeTrailLine = null; // Reset the function reference
      if (rootState.planTrail?.planMode) {
        console.log("Click ignored, plan mode active."); // DEBUG LOG
        return;
      }

      // *** GENERATE layersToQuery INSIDE the handler ***
      const layersToQuery: string[] = [];
      // Read FRESH state here
      state.activeSources.forEach(src => {
        layersToQuery.push(src); // Assumes layer ID matches source ID
        // layersToQuery.push(`${src}-border`); // Optional: if borders are clickable
      });
      if (state.hiikerTrailsActive) { // Read FRESH state here
        layersToQuery.push('hiiker-trails-main-layer'); // Adjust if your layer name is different
        // layersToQuery.push('hiiker-trails-border-layer');
      }
      // Add any other logic for dynamically determining clickable layers based on current state
      state.miniLoader = false
      if (layersToQuery.length === 0) {
        console.log("Click occurred, but no layers are active/queryable based on current state."); // DEBUG LOG
        window.trailCardClearData?.();
        dispatch('removeLineLayer');
        return;
      }
      // --- Optional but Recommended: Filter against actual map layers ---
      // This defends against brief timing issues where state says a layer is active,
      // but it hasn't quite been added to the map yet, or was just removed.
      const currentlyQueryableLayers = layersToQuery.filter(layerId => window.map?.getLayer(layerId));

      if (currentlyQueryableLayers.length === 0) {
        console.log("Click occurred, state indicates layers should be queryable, but none currently exist on map."); // DEBUG LOG
        window.trailCardClearData?.();
        dispatch('removeLineLayer');
        return;
      }
      // --- End Optional Filter ---


      console.log("Map click: Querying layers based on current state:", currentlyQueryableLayers); // Use filtered list

      const bbox: [[number, number], [number, number]] = [
        [e.point.x - HIT_RADIUS, e.point.y - HIT_RADIUS],
        [e.point.x + HIT_RADIUS, e.point.y + HIT_RADIUS]
      ];

      // Use the dynamically generated (and potentially filtered) list
      const feats = window.map!.queryRenderedFeatures(bbox, { layers: currentlyQueryableLayers });

      if (feats.length === 0) {
        console.log("Map click: No features found in query area."); // DEBUG LOG
        window.trailCardClearData?.();
        dispatch('removeLineLayer'); // Clear any previously selected individual line
        return;
      }

      const f = feats[0];
      console.log("Map click: Feature found:", f); // DEBUG LOG

      let logicalSource = f.source;
      // @ts-ignore
      if (f.layer.id === 'hiiker-trails-main-layer' || f.layer.id === 'hiiker-trails-border-layer') {
        logicalSource = 'hiiker-trails-source';
      }
      // Add similar mappings if other layer IDs don't match logical source IDs

      if (f.properties && logicalSource) {
        dispatch('addFeatureFromClick', { source: logicalSource, properties: f.properties });
        window.removeTrailLine = () => {
          dispatch('removeLineLayer'); // Clear any previously selected individual line
          window.removeTrailLine = null; // Reset the function reference
        };
      } else {
        console.warn("Clicked feature missing properties or logical source could not be determined:", f); // DEBUG LOG
      }
    };

    // Attach the handler (which now calculates layers internally)
    window.map.on('click', mapClickHandlerRef);
    console.log("Attached new map click handler (calculates query layers on click)."); // DEBUG LOG
  },
};

// ─────────────────────────── Getters ────────────────────────────
const getters: GetterTree<BaseMapDataOverlaysState,RootState> = {
  // Checks the persisted state
  isSourceActive:(s)=>(src:string)=>s.activeSources.includes(src),
  // No changes needed here
  minZoomForSource:()=>src=>src==='get_published_trails'?10:5,
};

// ─────────────────────────── Export module ──────────────────────
const mapDataOverlaysModule: Module<BaseMapDataOverlaysState,RootState> = {
  namespaced:true, state, mutations, actions, getters,
};
export default mapDataOverlaysModule;
